import { CameraSwitchIcon, ChevronUpIcon, SparklesIcon } from './ui/icons';
import { useAiAvailability } from '../hooks/useAiAvailability';
import { AiAvailabilityTooltip } from './ui/AiAvailabilityTooltip';
import { AiCreditTooltip } from './ui/AiCreditTooltip';
import SwipeSwitch from './ui/SwipeSwitch';
import { recordKey } from '../features/collection/multiCollectionIdentity';
import React, { useEffect, useRef, useState } from 'react';
import { useFeedback } from '../features/feedback/FeedbackProvider';
import { logger } from '../services/logger';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputLabel,
} from '@mui/material';
import {
  imageAnalysisService,
  mergeImageKeywords,
  ImageAnalysisAuthority,
  ImageAnalysisIntent,
} from '../services/imageAnalysisService';
import { LocationPicker } from '../features/locations/components/LocationPicker';
import { LocationUpdate } from '../features/locations/components/LocationEditorDialog';
import { Location } from '../domain/location';
import ImagePicker from './ImagePicker';
import { ResponsiveDialog } from './ui/ResponsiveDialog';
import { DialogHeader } from './ui/DialogHeader';
import { DialogActions } from './ui/DialogActions';
import { QuantityControl } from './ui/QuantityControl';
import { usePhoneLayout } from '../hooks/usePhoneLayout';
import { useCameraStream } from '../features/rapidCapture/useCameraStream';
import { captureVideoFrame } from '../features/rapidCapture/captureFrame';
import {
  requestDeviceOrientationAccess,
  useDeviceRoll,
} from '../features/rapidCapture/useDeviceRoll';
import { recordRapidCaptureMetric } from '../features/rapidCapture/rapidCaptureMetrics';
import { RapidCaptureDraft } from '../features/rapidCapture/rapidCaptureTypes';

interface AddItemModalProps {
  tagControl?: React.ReactNode;
  tagsPending?: boolean;
  tagIds?: string[];
  open: boolean;
  onClose: () => void;
  keywords: string;
  setKeywords: (value: string) => void;
  location: string;
  setLocation: (value: string) => void;
  qty: number | string;
  setQty: (value: number | string) => void;
  onPendingChange?: (pending: boolean) => void;
  /** Quickfire shots are saved as they are taken, so nothing is left unsaved. */
  onRapidCaptureChange?: (rapid: boolean) => void;
  handleSave: (analyzeImage?: boolean) => Promise<boolean>;
  handleSaveAndAddAnother: (analyzeImage?: boolean) => Promise<boolean>;
  locations: Location[];
  setShowAddLocationModal: (value: boolean) => void;
  image: string | null;
  lastUsedLocation: string;
  compressionInProgress: boolean;
  compressionProgress: number;
  handleImageCapture: (file: File) => void;
  handleClearImage: () => void;
  updateLastUsedLocation?: (location: string) => Promise<void>;
  saveLocation: (update: LocationUpdate) => Promise<boolean>;
  deleteLocation: (location: Location) => Promise<boolean>;
  analysisAvailable?: boolean;
  aiLocationRequired?: boolean;
  aiAuthority?: ImageAnalysisAuthority;
  pendingImage?: Blob | null;
  enqueue?: (draft: RapidCaptureDraft) => Promise<unknown>;
  /** Replaces the AI keyword control, e.g. where AI is not offered at all. */
  aiNotice?: React.ReactNode;
}

let beepContext: AudioContext | undefined;

/** One shared context: creating one per press is slow on mobile. Call from a
 * user gesture first (primeBeepSound) so iOS allows playback later.
 */
function primeBeepSound(): AudioContext | undefined {
  try {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextClass) return undefined;
    beepContext ??= new AudioContextClass();
    if (beepContext.state === 'suspended') void beepContext.resume();
    return beepContext;
  } catch {
    return undefined;
  }
}

function playBeepSound() {
  try {
    const ctx = primeBeepSound();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  } catch {
    // Ignore audio errors if blocked by browser policy
  }
}

const AddItemModal: React.FC<AddItemModalProps> = ({
  open,
  tagControl,
  tagsPending = false,
  tagIds,
  onClose,
  keywords,
  setKeywords,
  location,
  setLocation,
  qty,
  setQty,
  onPendingChange,
  onRapidCaptureChange,
  handleSave,
  handleSaveAndAddAnother,
  locations,
  setShowAddLocationModal,
  image,
  lastUsedLocation,
  compressionInProgress,
  compressionProgress,
  handleImageCapture,
  handleClearImage,
  updateLastUsedLocation,
  saveLocation,
  deleteLocation,
  enqueue,
  analysisAvailable = true,
  aiLocationRequired = false,
  aiAuthority,
  pendingImage,
  aiNotice,
}) => {
  const { notify } = useFeedback();
  const capturePending = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    onPendingChange?.(open && isSaving);
  }, [onPendingChange, open, isSaving]);
  const [isRapidCapture, setIsRapidCapture] = useState(false);
  useEffect(() => {
    onRapidCaptureChange?.(open && isRapidCapture);
  }, [onRapidCaptureChange, open, isRapidCapture]);
  const [isAiKeywords, setIsAiKeywords] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisSummary, setAnalysisSummary] = useState('');
  const [suggestions, setSuggestions] = useState<string | null>(null);
  const ai = useAiAvailability(
    open && Boolean(image) && analysisAvailable,
    aiAuthority,
  );
  const intentRef = useRef<ImageAnalysisIntent | null>(null);
  const requestVersion = useRef(0);
  const contextRef = useRef({
    open,
    isRapidCapture,
    image,
    pendingImage,
    aiAuthority,
  });
  contextRef.current = {
    open,
    isRapidCapture,
    image,
    pendingImage,
    aiAuthority,
  };
  const phoneLayout = usePhoneLayout();
  const keywordsId = React.useId();
  const camera = useCameraStream(open && isRapidCapture);
  const { rollRef: deviceRollRef, roll: deviceRoll } = useDeviceRoll(
    open && isRapidCapture,
  );
  const cameraContainerRef = useRef<HTMLDivElement>(null);

  const handleModalClose = () => {
    if (!capturePending.current && !isSaving) onClose();
  };
  useEffect(() => {
    if (!open) {
      setIsRapidCapture(false);
      setIsAiKeywords(false);
    }
  }, [open]);
  // Quickfire is on by default each time the modal opens on a phone.
  const quickfireDefault = Boolean(enqueue) && phoneLayout;
  useEffect(() => {
    if (open && quickfireDefault) setIsRapidCapture(true);
  }, [open, quickfireDefault]);
  useEffect(() => {
    requestVersion.current += 1;
    intentRef.current = null;
    setSuggestions(null);
    setAnalysisSummary('');
    setAnalysisLoading(false);
  }, [
    open,
    isRapidCapture,
    image,
    pendingImage,
    aiAuthority?.userId,
    aiAuthority?.collectionId,
    aiAuthority?.membershipVersion,
  ]);

  const generateKeywords = async () => {
    if (
      !ai.allowed ||
      !aiAuthority ||
      !pendingImage ||
      analysisLoading ||
      isRapidCapture ||
      isSaving ||
      compressionInProgress
    )
      return;
    const version = requestVersion.current;
    const active = () =>
      requestVersion.current === version &&
      contextRef.current.open &&
      !contextRef.current.isRapidCapture &&
      contextRef.current.pendingImage === pendingImage &&
      contextRef.current.aiAuthority?.userId === aiAuthority.userId &&
      contextRef.current.aiAuthority?.collectionId ===
        aiAuthority.collectionId &&
      contextRef.current.aiAuthority?.membershipVersion ===
        aiAuthority.membershipVersion;
    setAnalysisLoading(true);
    try {
      if (!intentRef.current) {
        const capability = ai.capability;
        if (!active()) return;
        if (!capability?.allowed) return;
        intentRef.current = {
          operationId: crypto.randomUUID(),
          costVersion: capability.costVersion,
          maxCredits: capability.credits,
        };
      }
      const result = await imageAnalysisService.generate(
        aiAuthority,
        {
          kind: 'received-bytes',
          image: pendingImage,
          skipSavedAnalysis: true,
        },
        intentRef.current,
      );
      if (!active()) return;
      setSuggestions(
        result.keywords.length ? result.keywords.join(', ') : null,
      );
      setAnalysisSummary(
        result.keywords.length ? '' : 'No useful keywords found.',
      );
      intentRef.current = null;
      ai.refresh();
    } catch (error) {
      if (!active()) return;
      const failure = error as {
        code?: string;
        details?: { code?: string; refunded?: boolean };
      };
      if (
        (failure.details?.code || failure.code) === 'confirmation-required' ||
        failure.details?.refunded === true
      ) {
        intentRef.current = null;
        ai.refresh();
      }
      if (
        [
          'role-required',
          'insufficient-credits',
          'collection-unavailable',
        ].includes(failure.details?.code || failure.code || '')
      )
        ai.refresh();
      notify(
        error instanceof Error
          ? error.message
          : 'Keyword generation could not finish.',
        'error',
      );
    } finally {
      if (active()) setAnalysisLoading(false);
    }
  };

  // Initialize values when modal is opened
  useEffect(() => {
    // Initialize values when modal is first opened and no values are set
    if (open && !keywords && !location && !image) {
      setLocation(lastUsedLocation || '');
      setQty(1);
    }
  }, [
    open,
    setKeywords,
    setLocation,
    setQty,
    lastUsedLocation,
    keywords,
    location,
    image,
  ]);

  const handleSaveClick = async () => {
    if (isSaving || tagsPending) return;
    setIsSaving(true);
    try {
      await handleSave(false);
    } catch (error) {
      logger.error('Error saving item:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndAddAnotherClick = async () => {
    if (isSaving || tagsPending) return;
    setIsSaving(true);
    try {
      await handleSaveAndAddAnother(false);
    } catch (error) {
      logger.error('[Rapid-Add] Error saving item:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRapidAddItem = async () => {
    if (
      capturePending.current ||
      isSaving ||
      tagsPending ||
      !enqueue ||
      !camera.ready ||
      !camera.videoRef.current
    )
      return;
    const selectedLocation = locations.find((entry) =>
      entry.collectionId
        ? recordKey(entry) === location
        : entry.name === location,
    );
    const numericQty = Number(qty);
    if (!selectedLocation || !Number.isFinite(numericQty) || numericQty < 1)
      return;
    const pressedAt = performance.now();
    capturePending.current = true;
    setIsSaving(true);
    const busyStartedAt = performance.now();
    primeBeepSound();
    try {
      const image = await captureVideoFrame(
        camera.videoRef.current,
        camera.facingMode === 'user',
        cameraContainerRef.current?.clientWidth ?? 0,
        cameraContainerRef.current?.clientHeight ?? 0,
        deviceRollRef.current,
      );
      await enqueue({
        image,
        keywords,
        location: selectedLocation.name,
        locationId: recordKey(selectedLocation),
        qty: numericQty,
        analyzeImage: analysisAvailable && isAiKeywords,
        ...(tagIds === undefined ? {} : { tagIds }),
      });
      playBeepSound();
      recordRapidCaptureMetric('capture-total', performance.now() - pressedAt, {
        ai: analysisAvailable && isAiKeywords,
      });
      // The remembered location is a convenience; it must not hold the shutter.
      void Promise.resolve(updateLastUsedLocation?.(location)).catch(
        () => undefined,
      );
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : 'The capture could not be queued.',
        'error',
      );
    } finally {
      recordRapidCaptureMetric(
        'shutter-busy',
        performance.now() - busyStartedAt,
        { analyzeImage: isAiKeywords },
      );
      capturePending.current = false;
      setIsSaving(false);
    }
  };

  const rapidCaptureControl = enqueue && (
    <FormControlLabel
      control={
        <SwipeSwitch
          checked={isRapidCapture}
          disabled={isSaving || tagsPending || compressionInProgress}
          onChange={(e) => {
            if (e.target.checked) void requestDeviceOrientationAccess();
            setIsRapidCapture(e.target.checked);
          }}
          color="primary"
          size="small"
          data-testid="rapid-capture-toggle"
        />
      }
      label={
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, fontStyle: 'italic' }}
        >
          Quickfire
        </Typography>
      }
      labelPlacement="start"
      sx={{ m: 0 }}
    />
  );

  const locationNote = (
    <Typography
      variant="caption"
      color="text.secondary"
      role="note"
      sx={{ flexBasis: '100%', textAlign: 'center' }}
    >
      Choose a location to add this item.
    </Typography>
  );

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleModalClose}
      maxWidth="md"
      fullScreenOnPhone
      aria-labelledby="add-item-title"
    >
      <DialogHeader
        id="add-item-title"
        title="Add item"
        gap={phoneLayout ? 1 : 2}
        action={rapidCaptureControl}
        onClose={handleModalClose}
      />
      <Box
        data-testid="add-item-layout"
        sx={{
          p: phoneLayout ? 2 : 3,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: phoneLayout ? 'column' : 'row',
          alignItems: 'flex-start',
          gap: { xs: 2.5, sm: 3 },
        }}
      >
        <Box
          sx={{
            width: phoneLayout ? '100%' : 'min(42%, 360px)',
            flexShrink: 0,
          }}
        >
          {isRapidCapture ? (
            <Box
              ref={cameraContainerRef}
              data-testid="rapid-camera-container"
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / 1',
                flexShrink: 0,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#111',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="video"
                ref={camera.videoRef}
                data-testid="rapid-camera-preview"
                muted
                playsInline
                autoPlay
                aria-hidden
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform:
                    [
                      deviceRoll ? `rotate(${-deviceRoll}deg)` : '',
                      camera.facingMode === 'user' ? 'scaleX(-1)' : '',
                    ]
                      .filter(Boolean)
                      .join(' ') || 'none',
                  transition: 'transform 0.2s ease',
                }}
              />
              {camera.starting && (
                <CircularProgress sx={{ position: 'absolute' }} />
              )}
              {camera.error && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      size="small"
                      onClick={() => {
                        void camera.start();
                      }}
                    >
                      Retry
                    </Button>
                  }
                  sx={{ position: 'absolute', m: 1, fontSize: '0.75rem' }}
                >
                  {camera.error}
                </Alert>
              )}
              {camera.cameraCount > 1 && (
                <IconButton
                  aria-label="Switch camera"
                  onClick={camera.switchCamera}
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover:not(.Mui-disabled)': {
                      bgcolor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <CameraSwitchIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : (
            <ImagePicker
              navigationKey="inventory.add.image"
              active={open}
              onImage={handleImageCapture}
              image={image}
              compressionInProgress={compressionInProgress}
              compressionProgress={compressionProgress}
              onClear={handleClearImage}
            />
          )}
        </Box>

        <Box
          component="fieldset"
          disabled={isSaving}
          sx={{
            border: 0,
            p: 0,
            m: 0,
            flex: 1,
            minWidth: 0,
            width: phoneLayout ? '100%' : 'auto',
            display: 'grid',
            alignContent: 'start',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 2.5,
          }}
        >
          <Box>
            {isRapidCapture && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <InputLabel htmlFor={keywordsId} sx={{ mb: 0 }}>
                  Keywords
                </InputLabel>
                {/* Align the visible controls with the label, accounting for touch-target padding. */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0,
                    height: 0,
                    flexShrink: 0,
                    transform: 'translateY(8px)',
                  }}
                >
                  <FormControlLabel
                    label={
                      <SparklesIcon
                        aria-hidden="true"
                        sx={{
                          fontSize: 18,
                          color: isAiKeywords
                            ? 'primary.main'
                            : 'text.secondary',
                        }}
                      />
                    }
                    labelPlacement="start"
                    sx={{ m: 0, minHeight: 44 }}
                    control={
                      <SwipeSwitch
                        checked={isAiKeywords}
                        disabled={
                          !analysisAvailable ||
                          isSaving ||
                          compressionInProgress
                        }
                        onChange={(e) => setIsAiKeywords(e.target.checked)}
                        inputProps={{ 'aria-label': 'AI Keywords' }}
                        color="primary"
                        size="small"
                        data-testid="ai-keywords-toggle"
                        sx={{ minHeight: 44 }}
                      />
                    }
                  />
                  <AiCreditTooltip
                    authority={aiAuthority}
                    freeUses={ai.capability?.freeAiUses}
                    description="Generate keywords."
                    label="About AI keywords"
                    showPreviouslyAnalysedNote={false}
                  />
                </Box>
              </Box>
            )}
            <TextField
              id={keywordsId}
              label={isRapidCapture ? undefined : 'Keywords'}
              variant="outlined"
              value={keywords}
              placeholder="Unnamed"
              onChange={(e) => setKeywords(e.target.value)}
              fullWidth
              multiline
            />
          </Box>

          {!isRapidCapture && analysisSummary && (
            <Typography role="status">{analysisSummary}</Typography>
          )}
          {!isRapidCapture && suggestions !== null && (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <ChevronUpIcon
                aria-hidden="true"
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                  justifySelf: 'center',
                }}
              />
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <TextField
                  label="Suggested keywords"
                  fullWidth
                  multiline
                  value={suggestions}
                  onChange={(event) => setSuggestions(event.target.value)}
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
                    gap: 1,
                    '& .MuiButton-root': {
                      minWidth: 0,
                      minHeight: 44,
                      borderRadius: 1,
                      px: { xs: 0.75, sm: 1.5 },
                      whiteSpace: 'normal',
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                    },
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    size="small"
                    disabled={!suggestions.trim()}
                    onClick={() => {
                      setKeywords(mergeImageKeywords(keywords, suggestions));
                      setSuggestions(null);
                    }}
                  >
                    Add to keywords
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={!suggestions.trim()}
                    onClick={() => {
                      setKeywords(suggestions);
                      setSuggestions(null);
                    }}
                  >
                    Replace keywords
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ color: 'text.secondary' }}
                    onClick={() => setSuggestions(null)}
                  >
                    Dismiss
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          <LocationPicker
            navigationKey="inventory.add.location"
            label="Location"
            value={location}
            onChange={setLocation}
            locations={locations}
            onAdd={() => setShowAddLocationModal(true)}
            onSaveLocation={async (update) => {
              const succeeded = await saveLocation(update);
              if (succeeded && updateLastUsedLocation)
                await updateLastUsedLocation(
                  update.collectionId ? recordKey(update) : update.name,
                );
              return succeeded;
            }}
            onDeleteLocation={deleteLocation}
          />

          {tagControl}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: 1,
            }}
          >
            <QuantityControl value={qty} onChange={setQty} />
            {!isRapidCapture && aiNotice}
            {!isRapidCapture && !aiNotice && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                <AiAvailabilityTooltip
                  reason={
                    !image
                      ? 'Add a photo to generate keywords.'
                      : compressionInProgress
                        ? 'Wait for the photo to finish processing.'
                        : aiLocationRequired
                          ? 'Choose a location first'
                          : ai.reason
                  }
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={generateKeywords}
                    disabled={
                      !ai.allowed ||
                      !aiAuthority ||
                      !pendingImage ||
                      !image ||
                      compressionInProgress ||
                      analysisLoading ||
                      isSaving
                    }
                    startIcon={
                      analysisLoading ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <SparklesIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      px: 1,
                      fontSize: '0.75rem',
                      '& .MuiButton-startIcon': { mr: 0.5 },
                    }}
                  >
                    Generate keywords
                  </Button>
                </AiAvailabilityTooltip>
                <AiCreditTooltip
                  authority={aiAuthority}
                  freeUses={ai.capability?.freeAiUses}
                  showPreviouslyAnalysedNote={false}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {isRapidCapture ? (
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          {!location.trim() && locationNote}
          <Button
            variant="contained"
            color="primary"
            onClick={handleRapidAddItem}
            disabled={
              isSaving ||
              tagsPending ||
              !camera.ready ||
              !location.trim() ||
              !Number.isFinite(Number(qty)) ||
              Number(qty) < 1
            }
            fullWidth
            size="medium"
            data-testid="rapid-add-item-button"
          >
            {isSaving ? 'Adding...' : 'Add Item'}
          </Button>
        </DialogActions>
      ) : (
        <DialogActions
          sx={{
            pl: phoneLayout ? 2 : 'calc(min(42%, 360px) + 56px)',
            flexWrap: 'wrap',
          }}
        >
          {!location.trim() && locationNote}
          <Button
            variant="outlined"
            onClick={handleSaveAndAddAnotherClick}
            sx={{ flex: 1 }}
            disabled={
              isSaving || tagsPending || !location || compressionInProgress
            }
            size="small"
          >
            {isSaving
              ? 'Saving...'
              : compressionInProgress
                ? 'Optimizing Image...'
                : 'Save & Add Another'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            sx={{ flex: 1 }}
            disabled={
              isSaving || tagsPending || !location || compressionInProgress
            }
            size="small"
          >
            {isSaving ? 'Saving...' : 'Save & Close'}
          </Button>
        </DialogActions>
      )}
    </ResponsiveDialog>
  );
};

export default AddItemModal;
